<template>
  <SearchableModel
    model-name="Registration"
    with-search
    with-export
    with-column-picker
    queued-export
    :search-placeholder="$t('Search by name, email, company, phone number, or badge number')"
    :base-filters="baseFilters"
    :available-filters="availableFilters"
    :sort-options="sortOptions"
    :push-state="pushState"
    :class="props.class"
  >
    <template v-if="$slots['header-title']" #header-title>
      <slot name="header-title"></slot>
    </template>

    <template #default="{ models, search }">
      <div class="mx-auto table-responsive my-2">
        <table v-if="models" class="table table-hover">
          <thead class="tw:text-left tw:text-sm tw:bg-zinc-50">
            <tr>
              <th>{{ $t('First name') }}</th>
              <th>{{ $t('Last name') }}</th>
              <th>{{ $t('Email') }}</th>
              <th>{{ $t('Phone') }}</th>
              <th>{{ $t('Event') }}</th>
              <th>{{ $t('Status') }}</th>
              <th v-if="isDeduplicated(models)" class="tw:text-right">{{ $t('Registrations') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="registration in models" :key="registration.id" class="tw:relative">
              <td class="tw:align-middle">
                <a v-if="registration.links.show" :href="registration.links.show" class="tw:font-bold">
                  <span class="tw:absolute tw:inset-0" aria-hidden="true"></span>
                  <text-highlight :query="search" :splitBySpace="true">{{ registration.firstname }}</text-highlight>
                </a>
                <text-highlight v-else :query="search" :splitBySpace="true">{{ registration.firstname }}</text-highlight>
              </td>
              <td class="tw:align-middle">
                <text-highlight :query="search" :splitBySpace="true">{{ registration.lastname }}</text-highlight>
              </td>
              <td class="tw:align-middle">
                <text-highlight :query="search" :splitBySpace="true">{{ registration.email ?? '' }}</text-highlight>
              </td>
              <td class="tw:align-middle tw:whitespace-nowrap">{{ registration.phone }}</td>
              <td class="tw:align-middle">{{ registration.tour.name }}</td>
              <td class="tw:align-middle">
                <RegistrationStatus class="tw:text-xl" :status="registration.status" />
              </td>
              <td v-if="isDeduplicated(models)" class="tw:align-middle tw:text-right tw:tabular-nums">
                {{ registration.registrations_count }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </SearchableModel>
</template>

<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import type { FilterDefinition } from '@/components/filters/filters';
import SearchableModel from '@/components/searchable-model/SearchableModel.vue';
import RegistrationStatus from '@/components/registration/RegistrationStatus.vue';

const props = withDefaults(defineProps<{
  class?: HTMLAttributes['class'],
  availableFilters?: FilterDefinition[],
  baseFilters?: Record<string, unknown>,
  pushState?: boolean,
}>(), {
  class: undefined,
  availableFilters: () => [],
  baseFilters: () => ({}),
  pushState: false,
});

const sortOptions = [
  { value: 'name', label: 'Name' },
  { value: 'created_at', label: 'Date' },
];

/** The API only counts merged registrations when a deduplication rule is applied. */
function isDeduplicated(models: Array<{ registrations_count: number | null }>): boolean {
  return models.some((registration) => registration.registrations_count !== null);
}
</script>
